import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "search-container" }
const _hoisted_4 = ["value"]
const _hoisted_5 = ["rows", "columns", "selectable", "server-side", "total-items", "current-page", "loading"]

import { EPINIO_TYPES } from '../types';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { computed, onMounted, onUnmounted, ref, watchEffect, watch } from 'vue';
import { startPolling, stopPolling } from '../utils/polling';
import Masthead from '@shell/components/ResourceList/Masthead';
import { makeEmptyCell, makeNameLinks, makeActionMenu, overrideTableRows } from '../utils/table-formatters';
import ConfigurationModal from '../components/configuration/ConfigurationModal.vue';
import ConfigurationDeleteModal from '../components/configuration/ConfigurationDeleteModal.vue';
import BulkDeleteModal from '../components/BulkDeleteModal.vue';
import { debounce } from 'lodash';


export default /*@__PURE__*/_defineComponent({
  __name: 'configurations',
  props: {
    schema: {}
  },
  setup(__props: any) {

const store = useStore();
const router = useRouter();

 // Keep for compatibility

const resource: string = EPINIO_TYPES.CONFIGURATION;

const configModal = ref<InstanceType<typeof ConfigurationModal> | null>(null);
const deleteModal = ref<InstanceType<typeof ConfigurationDeleteModal> | null>(null);
const bulkDeleteModal = ref<InstanceType<typeof BulkDeleteModal> | null>(null);
const tableEl = ref<any>(null);
const selectedRows = ref<any[]>([]);
const windowWidth = ref(window.innerWidth);
const onResize = () => { windowWidth.value = window.innerWidth; };
const displayRows = ref<any[]>([]);

const paginationMeta = computed(() => store.getters['epinio/paginationMeta'](resource));
const currentPage = computed(() => store.getters['epinio/currentPaginationPage'](resource));

const paginating = ref(false);

const searchQuery = ref<string>('');

async function goToPage(page: number) {
  const meta = paginationMeta.value;

  if (meta && (page < 1 || page > meta.totalPages)) return;
  paginating.value = true;
  try {
    await store.dispatch('epinio/goToPage', { type: resource, page });
  } finally {
    paginating.value = false;
  }
}

const onSearch = debounce(async (query: string) => {
  paginating.value = true;
  try {
    await store.dispatch('epinio/search', { type: resource, query });
  } finally {
    paginating.value = false;
  }
}, 500);

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

onMounted(async () => {
  window.addEventListener('resize', onResize);
  paginating.value = true;
  try {
    await Promise.all([
      store.dispatch('epinio/me'),
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.CONFIGURATION }),
      // Bound Applications/Service columns cross-reference these; fetch them
      // directly so they're populated on first load instead of depending on
      // another page (Applications/Services) having fetched them already.
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP }),
      store.dispatch('epinio/findAll', { type: EPINIO_TYPES.SERVICE_INSTANCE }),
    ]);
  } finally {
    paginating.value = false;
  }
  startPolling(['configurations', 'applications', 'services'], store);
});

onUnmounted(() => {
  window.removeEventListener('resize', onResize);
  stopPolling(['configurations', 'applications', 'services']);
});

const handleCreateClick = () => {
  configModal.value?.openCreate();
};

// Strict RBAC: only show Create when user has configuration write (hides for view_only)
const canCreateConfiguration = computed(() => {
  const can = store.getters['epinio/can'];
  const perms = store.getters['epinio/permissions']?.();

  if (!can || !perms || Object.keys(perms).length === 0) {
    return false;
  }

  return can('configuration_write') || can('configuration');
});

// Edit/Delete share the same permission as Create — anything that mutates
// a configuration requires configuration_write.
const canEdit = canCreateConfiguration;
const canDelete = canCreateConfiguration;

// Watch the active namespace cache key and update the active namespaces in the store
watch(
  () => {
    void store.state.activeNamespaceCacheKey;
    const active = store.state.activeNamespaceCache;
    return active ? Object.keys(active) : null;
  },
  async (namespacesArray) => {
    paginating.value = true;
    try {
      await store.dispatch('epinio/setActiveNamespaces', { type: resource, namespaces: namespacesArray });
    } finally {
      paginating.value = false;
    }
  
  },
  { immediate: true }
);

watchEffect(() => {
  const all = store.getters['epinio/all'](EPINIO_TYPES.CONFIGURATION) as any[];

  all.forEach((row: any) => { void row.status; void row.stateDisplay; void row.meta; void row.configuration; });

  // Filter empty rows that are added during delete
  const filtered = all.filter((row) => {
    if (!row.id) return false;
    return true;
  });

  const overrides = [
    {
      prop: 'availableActions',
      value: (row: any) => {
        const out: any[] = [];

        if (canEdit.value) {
          out.push({
            action:  'editConfigModal',
            label:   'Edit',
            enabled: row.configuration?.type === 'custom',
            icon:    'icon icon-edit',
          });
        }
        if (canDelete.value) {
          out.push({
            action:  'deleteConfigModal',
            label:   'Delete',
            enabled: row.configuration?.type === 'custom',
            icon:    'icon icon-trash',
            weight:  -10,
          });
        }

        return out;
      },
      conditionFn: () => true,
    },
    {
      prop:        'editConfigModal',
      value:       (row: any) => () => { configModal.value?.openEdit(row); },
      conditionFn: (row: any) => canEdit.value && row.configuration?.type === 'custom',
    },
    {
      prop:        'deleteConfigModal',
      value:       (row: any) => () => { deleteModal.value?.openDelete(row); },
      conditionFn: (row: any) => canDelete.value && row.configuration?.type === 'custom',
    },
  ];

  displayRows.value = [...overrideTableRows(filtered, overrides)];
});

// Auto-generated configurations (bound to a service instance) can't be
// individually deleted, so they're excluded from bulk selection too.
const isRowSelectable = (row: any) => row.configuration?.type === 'custom';

const handleSelectionChange = (event: CustomEvent) => {
  selectedRows.value = event.detail.selectedRows;
};

const handleBulkDeleteClick = () => {
  bulkDeleteModal.value?.openDelete(selectedRows.value);
};

const handleBulkDeleted = () => {
  selectedRows.value = [];
  tableEl.value?.clearSelection();
};

const handleNavigate = (event: CustomEvent) => {
  router.push(event.detail.url);
};

// NOTE: must be a named function declared here, not an inline arrow function
// in the template. Vue auto-unwraps top-level refs inside template
// expressions, so referencing `tableEl` directly in an inline template
// callback gives the already-unwrapped (initially null) value instead of
// the Ref object, and `tableEl.value = el` throws.
const setTableRef = (el: any) => {
  if (el) {
    tableEl.value = el;
    el.renderActions = makeActionMenu;
    el.rowSelectable = isRowSelectable;
  }
};

const allColumns = [
  {
    field: 'nameDisplay',
    label: 'Name',
    width: '200px',
    formatter: (_v: any, row: any) => {
      const el = document.createElement('a');

      el.textContent = row.nameDisplay || row.meta?.name || '';
      el.style.cursor = 'pointer';
      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        configModal.value?.openView(row);
      });

      return el;
    }
  },
  {
    field: 'namespace',
    label: 'Namespace',
    width: '100px',
  },
  {
    field: 'boundApps',
    label: 'Bound Applications',
    width: '200px',
    sortable: false,
    formatter: (_v: any, row: any) => makeNameLinks(
      row.configuration?.boundapps,
      { cluster: store.getters['clusterId'], namespace: row.meta?.namespace, resource: EPINIO_TYPES.APP },
      router
    )
  },
  {
    field: 'service',
    label: 'Service',
    width: '150px',
    sortable: false,
    formatter: (_v: any, row: any) => makeNameLinks(
      row.configuration?.origin ? [row.configuration.origin] : [],
      { cluster: store.getters['clusterId'], namespace: row.meta?.namespace, resource: EPINIO_TYPES.SERVICE_INSTANCE },
      router
    )
  },
  {
    field: 'variableCount',
    label: 'No. of Variables',
    width: '150px'
  },
  {
    field: 'configuration.user',
    label: 'Created By',
    width: '150px',
    formatter: (_v: any, row: any) => row.configuration?.user || makeEmptyCell()
  },
  {
    field: 'meta.createdAt',
    label: 'Age',
    width: '50px',
    formatter: 'age'
  }
];

// Drop lower-priority columns at smaller window widths
//   <1300px: hide Service and Created By
//   <1100px: also hide Age
const columns = computed(() => {
  const w = windowWidth.value;
  const hide = new Set<string>();

  if (w < 1300) {
    hide.add('service');
    hide.add('configuration.user');
  }
  if (w < 1100) {
    hide.add('meta.createdAt');
  }

  return allColumns.filter(col => !hide.has(col.field));
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Masthead), {
      schema: _ctx.schema,
      resource: _unref(resource)
    }, {
      extraActions: _withCtx(() => [
        (selectedRows.value.length)
          ? (_openBlock(), _createElementBlock("trailhand-button", {
              key: 0,
              variant: "destructive",
              size: "large",
              onClick: handleBulkDeleteClick
            }, [
              _cache[2] || (_cache[2] = _createElementVNode("trailhand-icon", { name: "trash" }, null, -1)),
              _createTextVNode("\n          Delete (" + _toDisplayString(selectedRows.value.length) + ")\n        ", 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      createButton: _withCtx(() => [
        (canCreateConfiguration.value)
          ? (_openBlock(), _createElementBlock("trailhand-button", {
              key: 0,
              variant: "primary",
              size: "large",
              onClick: handleCreateClick
            }, _toDisplayString(_ctx.t('generic.create')), 1))
          : (_openBlock(), _createElementBlock("div", _hoisted_2))
      ]),
      _: 1,
      __: [3]
    }, 8, ["schema", "resource"]),
    _cache[4] || (_cache[4] = _createTextVNode()),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("trailhand-text-input", {
        value: searchQuery.value,
        placeholder: "Search...",
        onTextInputChange: _cache[0] || (_cache[0] = (e) => searchQuery.value = e.detail.value)
      }, null, 40, _hoisted_4)
    ]),
    _cache[5] || (_cache[5] = _createTextVNode()),
    _createElementVNode("trailhand-table", {
      ref: setTableRef,
      rows: displayRows.value,
      columns: columns.value,
      searchable: false,
      selectable: _unref(canDelete),
      "server-side": !!paginationMeta.value,
      "total-items": paginationMeta.value?.totalItems ?? displayRows.value.length,
      "current-page": currentPage.value,
      loading: paginating.value,
      "key-field": "id",
      onNavigate: handleNavigate,
      onPageChange: _cache[1] || (_cache[1] = (e) => goToPage(e.detail.page)),
      onSelectionChange: handleSelectionChange
    }, null, 40, _hoisted_5),
    _cache[6] || (_cache[6] = _createTextVNode()),
    _createVNode(ConfigurationModal, {
      ref_key: "configModal",
      ref: configModal
    }, null, 512),
    _cache[7] || (_cache[7] = _createTextVNode()),
    _createVNode(ConfigurationDeleteModal, {
      ref_key: "deleteModal",
      ref: deleteModal
    }, null, 512),
    _cache[8] || (_cache[8] = _createTextVNode()),
    _createVNode(BulkDeleteModal, {
      ref_key: "bulkDeleteModal",
      ref: bulkDeleteModal,
      "resource-label": "configuration",
      "resource-type": _unref(resource),
      "show-unbind-notice": true,
      onSettled: handleBulkDeleted
    }, null, 8, ["resource-type"])
  ]))
}
}

})